import { useState } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MarketingFrame } from "../components/MarketingFrame";
import { Contact as ContactAPI } from "../lib/api";

const TOPICS = ["General question", "Pricing / billing", "Camera not working", "Sync to AIImageSuite.pro", "Partnership / press"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) return toast.error("EMAIL AND MESSAGE ARE REQUIRED");
    setSending(true);
    try {
      await ContactAPI.send(form);
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || err.message;
      toast.error(String(msg).toUpperCase().slice(0, 100));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full border-2 border-black px-3 py-3 font-mono text-sm bg-white focus:outline-none focus:ring-4 focus:ring-[#002FA7]";

  return (
    <MarketingFrame testid="contact-page">
      <section className="px-6 py-14 max-w-5xl mx-auto w-full grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-10">
        <div className="space-y-4">
          <p className="label-mono text-neutral-500">CONTACT</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]" data-testid="contact-headline">
            TALK TO A HUMAN.
          </h1>
          <p className="font-mono text-neutral-700">
            Billing issue, camera that won't show up, or a workflow question before a big shoot — send it here and we'll reply by email.
          </p>
          <ul className="label-mono text-neutral-600 space-y-2 pt-2">
            <li><Link to="/help/camera" className="underline underline-offset-4 hover:text-black" data-testid="contact-camera-help-link">CAMERA TROUBLESHOOTING GUIDE →</Link></li>
            <li><Link to="/pricing" className="underline underline-offset-4 hover:text-black">PRICING →</Link></li>
          </ul>
        </div>

        {sent ? (
          <div className="border-2 border-black p-8 flex flex-col items-start gap-3 bg-[#F8F9FA]" data-testid="contact-success">
            <CheckCircle size={40} weight="bold" className="text-green-700" />
            <p className="font-display font-black tracking-tighter text-2xl">MESSAGE RECEIVED</p>
            <p className="font-mono text-sm text-neutral-700">We'll reply to <span className="font-bold">{form.email}</span>. Meanwhile you can keep shooting.</p>
            <Link to="/sessions" className="btn-flat mt-2 px-5 py-2 text-sm" data-testid="contact-success-cta">Back to capture</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border-2 border-black p-6 space-y-4 bg-white" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1">
                <span className="label-mono text-neutral-500">NAME</span>
                <input className={field} value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Optional" />
              </label>
              <label className="space-y-1">
                <span className="label-mono text-neutral-500">EMAIL *</span>
                <input className={field} type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="you@studio.com" />
              </label>
            </div>
            <label className="space-y-1 block">
              <span className="label-mono text-neutral-500">TOPIC</span>
              <select className={field} value={form.topic} onChange={set("topic")} data-testid="contact-topic">
                {TOPICS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="space-y-1 block">
              <span className="label-mono text-neutral-500">MESSAGE *</span>
              <textarea className={`${field} min-h-[140px]`} required value={form.message} onChange={set("message")} data-testid="contact-message" placeholder="What's happening, and on which device / browser?" />
            </label>
            <button type="submit" disabled={sending} className="btn-flat w-full py-3 inline-flex items-center justify-center gap-2" data-testid="contact-submit">
              <PaperPlaneTilt size={18} weight="bold" />
              {sending ? "SENDING…" : "SEND MESSAGE"}
            </button>
          </form>
        )}
      </section>
    </MarketingFrame>
  );
}
